import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import styles from "./CarDetail.module.css";
import {
  FaArrowLeft,
  FaCog,
  FaUser,
  FaGasPump,
  FaPaintRoller,
  FaTachometerAlt,
} from "react-icons/fa";

import { Loading } from "./components/Loading";
import ImageModal from "../components/ImageModal";
import apiService from "../services/apiService";
import { buildCleanMapIframe, getMapUrl } from "./MyCars/Inspection";

const CarDetailScreen = () => {
  const { carId } = useParams();
  const [carDetails, setCarDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const fetchCarDetails = async (carId) => {
    setIsLoading(true);
    try {
      const response = await apiService.getCarDetailsPageData(carId);
      setCarDetails(response.data);
    } catch (error) {
      console.error("Error fetching car details:", error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCarDetails(carId);
  }, []);

  return (
    <div className="container">
      <button
        className={styles.backButton}
        onClick={() => window.history.back()}
      >
        <FaArrowLeft /> Back
      </button>
      <div className={styles.wrapper}>
        {isLoading && <Loading />}
        {!isLoading && !isError && (
          <>
            <CarDetails
              title={carDetails.title}
              regno={carDetails.regno}
              specs={carDetails.specs}
              rcstatus={carDetails.rcstatus}
              images={carDetails.images}
            />
            <BiddingDetails
              location={carDetails.location}
              start_date={carDetails.start_date}
              end_date={carDetails.end_date}
              YardMap={carDetails.YardMap}
            />
            <InteractionHistory 
              history={carDetails.interactionHistory} 
              carId={carId}
              onHistoryUpdate={() => fetchCarDetails(carId)}
            />
          </>
        )}
        {!isLoading && isError && <div>Something went wrong</div>}
      </div>
    </div>
  );
};

function CarDetails({ title, regno, rcstatus, specs, images }) {
  const [selectedImage, setSelectedImage] = useState(0);

  return (
    <div className={styles.carDetails}>
      <div className={styles.carBasicInfo}>
        <h2>{title}</h2>
        <div className={styles.carRegInfo}>
          <p className={styles.licensePlate}>{regno}</p>
          <p
            className={`${styles.rc} ${
              styles["rc" + (rcstatus?.toLowerCase() ? "yes" : "no")]
            }`}
          >
            RC ✓
          </p>
        </div>
      </div>
      <div className={styles.carImages}>
        <div className={styles.mainImage}>
          <img
            src={images[selectedImage]}
            alt={`Car Image ${selectedImage + 1}`}
          />
        </div>
        <div className={styles.thumbnailContainer}>
          <div className={styles.thumbnails}>
            {images.map((img, index) => (
              <img
                key={index}
                src={img}
                alt={`Thumbnail ${index + 1}`}
                className={
                  index === selectedImage
                    ? styles.selectedThumbnail
                    : styles.thumbnail
                }
                onClick={() => setSelectedImage(index)}
              />
            ))}
          </div>
        </div>
      </div>
      <div className={`${styles.carBasicInfo} ${styles.carSpecs}`}>
        <div className={styles.specItem}>
          <FaUser /> <span>{specs.ownership}</span>
        </div>
        <div className={styles.specItem}>
          <FaGasPump /> <span>{specs.fueltype}</span>
        </div>
        <div className={styles.specItem}>
          <FaTachometerAlt /> <span>{formatToKms(specs.kmsrun)}</span>
        </div>
        <div className={styles.specItem}>
          <FaPaintRoller /> <span>NA</span>
        </div>
        <div className={styles.specItem}>
          <FaCog /> <span>{specs.transmission || "NA"}</span>
        </div>
      </div>
    </div>
  );
}

function BiddingDetails({ location, start_date, end_date, YardMap }) {
  return (
    <div className={styles.biddingDetails}>
      <div className={styles.timeLeft}>
        <span>Time Left:</span>
        <LiveCountdown start_date={start_date} end_date={end_date} />
      </div>

      <div className={styles.location}>
        <div className={styles.mapContainer}>
          {console.log("Loch", YardMap)
    }
          {YardMap ? (
            <div
              className={styles.mapIframeWrapper}
              onClick={() => window.open(getMapUrl(YardMap), "_blank")}
              style={{ cursor: "pointer" }}
            >
              <div
                className={styles.mapIframe}
                style={{ pointerEvents: "none" }}
                dangerouslySetInnerHTML={{
                  __html: buildCleanMapIframe(YardMap) ||
                    '<div style="background:#f0f0f0; height:200px; display:flex; align-items:center; justify-content:center; color:#999; font-size:14px;">Map not available</div>',
                }}
              />
            </div>
          ) : (
            <img
              alt="Map not available"
              width="100%"
              src="/static/media/dummyMap.da25985b50820372fc6acc5f9e7b4327.svg"
            />
          )}
        </div>

        <div className={styles.locationInfo}>
          <img
            alt="Location"
            src="/static/media/locCard.65c09207a4ad7b5644b90b497de12478.svg"
            width="20px"
          />
          <p>{location}</p>
        </div>
      </div>
    </div>
  );
}

function InteractionHistory({ history, carId, onHistoryUpdate }) {
  const [message, setMessage] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const handleImageClick = (imageUrl, imageLabel) => {
    setSelectedImage({
      url: imageUrl,
      label: imageLabel
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
  };

  const handleSendMessage = async () => {
    if (!message.trim() || isSending) return;

    setIsSending(true);
    try {
      await apiService.sendChatMessage({
        carId,
        message: message.trim(),
        messageType: 'text'
      });
      
      // Clear the input
      setMessage("");
      
      // Refresh the history
      if (onHistoryUpdate) {
        onHistoryUpdate();
      }
      
      console.log("Message sent successfully");
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Failed to send message. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className={styles.interactionHistory}>
      <div className={styles.historyHeader}>
        <h2 className={styles.historyTitle}>History</h2>
        {history.length > 0 && (
          <button className={styles.loadHistoryButton}>
            Load Previous History
          </button>
        )}
      </div>

      {history.length === 0 && <p>No interactions yet.</p>}

      <div className={styles.timeline}>
        {history.map((item, idx) => (
          <div key={idx} className={styles.timelineItem}>
            <div className={styles.timelineDot}></div>
            {idx < history.length - 1 && (
              <div className={styles.timelineLine}></div>
            )}
            <div className={styles.timelineContent}>
              <div className={styles.timelineTime}>{formatDate(item.timestamp)}</div>
              <div className={styles.timelineText}>{item.log}</div>
              {(item.images || (item.details && item.details.imageUrls)) && (
                <div className={styles.timelineImages}>
                  {(item.images || item.details.imageUrls).map((img, imgIdx) => {
                    const imageUrl = typeof img === 'string' ? img : img.url;
                    const imageLabel = typeof img === 'string' ? '' : img.label || img.type || '';
                    
                    return (
                      <img
                        key={imgIdx}
                        src={imageUrl}
                        alt={imageLabel}
                        className={styles.timelineImage}
                        title={imageLabel}
                        onClick={() => handleImageClick(imageUrl, imageLabel)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className={styles.chatBox}>
        <div className={styles.chatInput}>
          <input
            type="text"
            placeholder="Send us your message here"
            className={styles.chatInputField}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isSending}
          />
          <button 
            className={styles.chatSendButton}
            onClick={handleSendMessage}
            disabled={isSending || !message.trim()}
          >
            {isSending ? (
              <div className={styles.spinner}></div>
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M18 2L9 11M18 2l-6 16-3-7-7-3 16-6z"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        </div>
      </div>
      
      <ImageModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        imageUrl={selectedImage?.url}
        imageAlt={selectedImage?.label}
        imageLabel={selectedImage?.label}
      />
    </div>
  );
}

export default CarDetailScreen;

function LiveCountdown({ start_date, end_date, className = "" }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const parseSafe = (s) => {
    if (!s) return NaN;
    const d = new Date(s);
    return Number.isFinite(d.getTime()) ? d : NaN;
  };

  const start = parseSafe(start_date);
  const end = parseSafe(end_date);

  // If either date is invalid, show NA
  if (!start || !end) {
    return (
      <h3 className={`text-xl font-semibold ${className}`} aria-live="polite">
        NA
      </h3>
    );
  }

  const nowDate = new Date(now);

  // Decide target and state label
  let label = "";
  let target = null;
  if (nowDate < start) {
    label = "Starts in";
    target = start;
  } else if (nowDate <= end) {
    label = "Ends in";
    target = end;
  } else {
    // already finished
    return (
      <h3 className={`text-xl font-semibold ${className}`} aria-live="polite">
        Ended
      </h3>
    );
  }

  let diff = target.getTime() - nowDate.getTime();
  if (diff < 0) diff = 0;

  const MS = {
    minute: 60 * 1000,
    hour: 60 * 60 * 1000,
    day: 24 * 60 * 60 * 1000,
  };

  const days = Math.floor(diff / MS.day);
  const hours = Math.floor((diff % MS.day) / MS.hour);
  const minutes = Math.floor((diff % MS.hour) / MS.minute);

  const pad = (n) => String(n).padStart(2, "0");

  // format: "1D 3h:15m" or "3h:05m" when days === 0
  const formatted =
    days > 0
      ? `${days}D ${hours}h:${pad(minutes)}m`
      : `${hours}h:${pad(minutes)}m`;

  return (
    <h3 className={className} aria-live="polite">
      {formatted}
    </h3>
  );
}

function formatToKms(numStr) {
  const num = Number(numStr);
  if (isNaN(num)) return "NA";

  return num.toLocaleString("en-IN") + " KMS";
}

function formatDate(isoString) {
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) throw new Error("Invalid date");

    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" });
    const hours = date.getHours() % 12 || 12;
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = date.getHours() >= 12 ? "PM" : "AM";

    const suffix =
      day % 10 === 1 && day !== 11
        ? "st"
        : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
        ? "rd"
        : "th";

    return `${day}${suffix} ${month} ${hours}:${minutes} ${ampm}`;
  } catch (e) {
    return "NA";
  }
}
